import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@staff-app/ui';
import { ArrowRight, Download, RefreshCw, Search } from 'lucide-react';
import {
  INTL_DEVICE_LABELS,
  INTL_DEVICE_MODELS,
  INTL_STAGES,
  INTL_STAGE_LABELS,
  STRIPE_INVOICE_STATUSES,
  type IntlDeviceModel,
  type IntlStage,
} from '@staff-app/shared-types';
import type { IntlRequest } from '@staff-app/shared-types';
import { refreshSummary } from '../refresh-summary';
import { IntlRequestDetailDrawer } from '../components/IntlRequestDetailDrawer';
import { IntlExportDialog } from '../components/IntlExportDialog';
import { IntlRequestTable } from '../components/IntlRequestTable';
import { RaiseInvoiceDialog } from '../components/RaiseInvoiceDialog';
import {
  INTL_PAGE_SIZE,
  INTL_PAGE_SIZE_OPTIONS,
  useCanFile,
  useCanFulfil,
  useIntlRequests,
  useRefreshIntlFulfilment,
  useRefreshIntlInvoices,
} from '../hooks/useIntlRequests';

const ALL = '__all__';

/**
 * International Requests — the THIRD section of Devices Order (requester, 2026-09-09), to the
 * right of Search Shopify Orders. `DeviceOrdersShell` owns the h1 + icon.
 *
 * WHY IT BELONGS IN THAT GROUP RATHER THAN ITS OWN DESTINATION. It is the same journey as its two
 * neighbours at a different stage — a hardware order that becomes an invoice and then a shipment
 * — and the shell already exists to hold exactly that progression. It also puts filing where the
 * people who file already are: a rep working a hardware deal is on Devices Order, not hunting a
 * separate sidebar entry.
 *
 * ITS ROLES ARE NOT ITS NEIGHBOURS' — and that is why the shell's route group had to be
 * restructured rather than simply gaining a child. The coupon queue and the Shopify lookup are
 * root/engineer/support; this tab is root/support/FULFILLMENT. Nested under the old single guard,
 * a `fulfillment` user would have been refused by the parent before this tab's own guard ever
 * ran — the same trap `routes/index.tsx` documents for Devices Fulfillment. So the parent now
 * admits the UNION and each child carries its real gate.
 *
 * ONE PAGE, NOT TWO TABS. An earlier design of this feature split it into a submit form and an
 * approver queue, because two roles do two different jobs. That split earned its keep when there
 * was an approval step with a compliance gate behind it; there is not one here. What remains is a
 * rep filing a request and somebody completing its address, and putting those on separate
 * destinations would mean the person who files cannot see what happened next.
 *
 * FORM-FIRST, LEDGER UNDER IT — the shape `DemoCouponsPage` argues for in its own docblock: "the
 * act comes first and the record follows". The card is FOLDED by default, so a rep who came to
 * check on IR-58 is not made to scroll past fifteen fields to reach the list.
 *
 * THE PAGE GROWS AND THE SHELL SCROLLS IT, unlike its two neighbours which pin themselves to the
 * viewport and scroll their tables inside. Same reason DemoCouponsPage grows: this stacks a tall
 * card on top of a ledger, and squeezing both into the viewport clipped the card on a laptop with
 * no way to reach what was cut off. `DeviceOrdersShell`'s outlet is
 * `min-h-0 flex-1 overflow-auto`, which does the scrolling and keeps the h1 and the tab row
 * pinned above it.
 */
export function IntlRequestsPage() {
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState<IntlStage | ''>('');
  const [deviceModel, setDeviceModel] = useState<IntlDeviceModel | ''>('');
  const [invoiceStatus, setInvoiceStatus] = useState('');
  const [mine, setMine] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState<number>(INTL_PAGE_SIZE);
  /**
   * Owned by the PAGE, not the table, for the reason DemoCouponsPage gives for the same choice:
   * raising an invoice refetches the list underneath, and a dialog held by a row would be at the
   * mercy of that re-render.
   */
  const [invoicing, setInvoicing] = useState<IntlRequest | null>(null);
  /**
   * The row whose detail panel is open (requester, 2026-09-09: "When I click the request, I want
   * to pop out the detail card in the right side").
   *
   * Held as an ID, not the row object, so the panel follows the LIST. Every mutation here
   * refetches `intlRequests`, and a captured object would keep showing the invoice status from
   * before the refresh — which is precisely the fact somebody opened the panel to read.
   */
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [sweep, setSweep] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  /**
   * ── REQUESTS TICKED FOR EXPORT (requester, 2026-09-17) ─────────────────────────────────────
   *
   * Held BY ID rather than by row, so a selection survives paging, filtering and a refetch — an
   * operator ticking three requests across two pages should get three rows, not whichever ones
   * happen to be rendered when they press Export.
   *
   * Cleared after a successful export: leaving it set is how the NEXT export silently produces
   * the previous one's file.
   */
  const [selectedForExport, setSelectedForExport] = useState<Set<number>>(new Set());
  const toggleForExport = (id: number) =>
    setSelectedForExport((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const canFulfil = useCanFulfil();
  const canFile = useCanFile();

  // Any filter change puts you back on page 1 — otherwise a narrowed result set can leave you on
  // a page that no longer exists, which reads as "no results".
  useEffect(() => {
    setPageIndex(0);
  }, [search, stage, deviceModel, invoiceStatus, mine, pageSize]);

  const { data, loading, error, refetch } = useIntlRequests(
    {
      search,
      stage: stage || undefined,
      deviceModel: deviceModel || undefined,
      invoiceStatus: (invoiceStatus || undefined) as never,
      mine,
    },
    pageIndex * pageSize,
    pageSize,
  );
  const page = data?.intlRequests;
  const items = page?.items ?? [];
  const total = page?.total ?? 0;
  const firstShown = total === 0 ? 0 : pageIndex * pageSize + 1;
  const lastShown = Math.min(total, pageIndex * pageSize + items.length);

  // Not `items.find(...) ?? null` alone: a filter change or a page turn can drop the open row
  // out of the list, and the panel should close rather than sit there showing a stale record.
  const selected = selectedId === null ? null : (items.find((r) => r.id === selectedId) ?? null);

  const [refreshAll, { loading: sweeping }] = useRefreshIntlInvoices();
  const [refreshFulfilment, { loading: sweepingMcf }] = useRefreshIntlFulfilment();

  /**
   * ONE Refresh, which asks Stripe AND Amazon and re-reads our table.
   *
   * Two requesters' decisions, four days apart, pointing the same way. 2026-09-09: "for the
   * refresh and check with stripe button should be one. I don't need two of it" — the re-read
   * and the Stripe sweep merged. 2026-09-16: it should "also allow refresh the Stripe and
   * Fulfilment - Amazon" — Amazon joined them.
   *
   * WHY THE AMAZON HALF HAD TO BE HERE. The button said "Refresh" and asked Stripe only, while
   * the one control that could bring a tracking number in was a small icon inside the drawer's
   * Amazon section. Whoever pressed the obvious button got a faithful re-render of the same
   * `Tracking —` however many times they pressed it, with nothing saying why. A button named
   * Refresh has to refresh what the page shows, and this page shows both systems.
   *
   * BOTH IN PARALLEL, AND NEITHER CAN COST THE OTHER. `Promise.allSettled`, not `all`: Stripe
   * being unreachable must not throw away Amazon's answer, and vice versa. Each half reports its
   * own counts, and a half that failed says so by name — "could not reach Stripe" and "could not
   * reach Amazon" send somebody to different places.
   */
  const onRefresh = async () => {
    setSweep(null);

    /**
     * ⚠️ THIS USED TO SKIP BOTH SWEEPS FOR NON-ROOT, and that was a bug with a very quiet symptom.
     *
     * The sweeps were root-only while support could already raise invoices, so a support rep
     * billed a customer and then had no way to learn whether it was paid: Refresh re-read the
     * table, re-rendered the same cached status, and said nothing. "The invoice is not
     * refreshing" is exactly what that looks like from the outside.
     *
     * The refreshes are now on the read tier, which is what three separate docblocks had claimed
     * all along ("the wider tier: asking costs nothing"). Refreshing is re-reading what you can
     * already see, so anyone who may see the row may ask for it to be current — and the button
     * does the same thing for everybody again.
     */
    const [stripe, amazon] = await Promise.allSettled([
      refreshAll({ variables: {} }),
      refreshFulfilment({ variables: {} }),
    ]);

    // The sentence itself is built by `refreshSummary`, which is pure and has its own spec —
    // four outcomes per half times two halves is the part with judgement in it, and this page
    // has no test harness.
    const summary = refreshSummary(
      stripe.status === 'fulfilled'
        ? { status: 'fulfilled', value: stripe.value?.data?.refreshIntlInvoices }
        : stripe,
      amazon.status === 'fulfilled'
        ? { status: 'fulfilled', value: amazon.value?.data?.refreshIntlFulfilment }
        : amazon,
    );
    if (summary) setSweep(summary);

    // The plain table re-read the operator pressed for cannot fail for either upstream's reason.
    // Only needed when BOTH halves failed — a successful one has already refetched the list.
    if (stripe.status === 'rejected' && amazon.status === 'rejected') {
      await refetch().catch(() => {});
    }
  };

  return (
    <div className="flex flex-col gap-6 p-6" data-testid="intl-requests-page">
      {/* No heading: the shell owns the page title, and the active tab already names this
          section. Only what is specific to international requests earns space here. */}
      <p className="text-sm text-muted-foreground">
        {'Hardware orders into markets the store does not serve. A rep files the request on the '}
        {'customer’s behalf — these arrive through a sales conversation, often from a prospect '}
        {'with no OptiSigns account, so there is no customer-facing form.'}
      </p>

      {/* NEW REQUESTS ARE FILED UNDER ALL INTERNATIONAL (Paul, 2026-10-29) — this tab keeps the
          requests already filed here, and the form gives way to a pointer. Drawn for whoever may
          file (root + support, `useCanFile`), never fulfilment, who ship rather than open orders. */}
      {canFile ? (
        <div
          className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm"
          data-testid="intl-file-in-air"
        >
          <span className="text-foreground">{'New international requests are filed in All International.'}</span>
          <Link
            to="/staff/order-devices/all-international"
            className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
            data-testid="intl-file-in-air-link"
          >
            {'File a request there'}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[280px] max-w-md flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="IR-62, company, contact, cus_… or in_…"
            className="pl-9"
            data-testid="intl-search"
          />
        </div>

        <Select
          value={stage || ALL}
          onValueChange={(v) => setStage(v === ALL ? '' : (v as IntlStage))}
        >
          <SelectTrigger className="w-[150px]" data-testid="intl-filter-stage">
            <SelectValue placeholder="Stage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{'All stages'}</SelectItem>
            {INTL_STAGES.map((s) => (
              <SelectItem key={s} value={s}>
                {INTL_STAGE_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={deviceModel || ALL}
          onValueChange={(v) => setDeviceModel(v === ALL ? '' : (v as IntlDeviceModel))}
        >
          <SelectTrigger className="w-[170px]" data-testid="intl-filter-model">
            <SelectValue placeholder="Item" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{'All items'}</SelectItem>
            {INTL_DEVICE_MODELS.map((m) => (
              <SelectItem key={m} value={m}>
                {INTL_DEVICE_LABELS[m]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* STRIPE's status, on its OWN filter — the second axis (requester, 2026-09-09). The
            stage says what we have done; this says what Stripe thinks of the money. Filtering
            both is how "invoiced but still unpaid" is expressed, which is the manager's working
            set and the thing a single folded status column could not say. */}
        <Select
          value={invoiceStatus || ALL}
          onValueChange={(v) => setInvoiceStatus(v === ALL ? '' : v)}
        >
          <SelectTrigger className="w-[170px]" data-testid="intl-filter-invoice-status">
            <SelectValue placeholder="Invoice status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{'Any invoice status'}</SelectItem>
            {/* Ours, not Stripe's — a row with no invoice is a real thing to look for. */}
            <SelectItem value="none">{'No invoice'}</SelectItem>
            {STRIPE_INVOICE_STATUSES.map((st) => (
              <SelectItem key={st} value={st}>
                {st}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* "Mine" is resolved server-side from the caller's token — there is deliberately no
            submitter box to type a colleague's address into. */}
        <Button
          variant={mine ? 'default' : 'outline'}
          onClick={() => setMine((v) => !v)}
          data-testid="intl-filter-mine"
          aria-pressed={mine}
        >
          {'Mine'}
        </Button>

        {/* ONE button — see `onRefresh`. It asks Stripe about every live invoice and re-reads the
            table, because the invoice status IS what the Invoice-draft / Invoiced stages are
            derived from: the auto-send finalizes a draft about an hour after filing with nobody
            doing anything, so a refresh that did not ask Stripe would faithfully re-display a
            stale answer. */}
        <Button
          variant="outline"
          onClick={onRefresh}
          disabled={sweeping || sweepingMcf || loading}
          data-testid="intl-refresh"
          title="Re-read the list, and ask Stripe about every live invoice and Amazon about every live fulfilment order"
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${sweeping || sweepingMcf || loading ? 'animate-spin' : ''}`}
          />
          {sweeping || sweepingMcf ? 'Refreshing…' : 'Refresh'}
        </Button>

        {/* ROOT + FULFILMENT only, mirroring the server tier: support can read every row here
            and cannot export them. `useCanFulfil` is exactly that pair. */}
        {canFulfil ? (
          <Button variant="outline" onClick={() => setExporting(true)} data-testid="intl-export-open">
            <Download className="mr-2 h-4 w-4" />
            {'Export'}
          </Button>
        ) : null}

        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-muted-foreground" data-testid="intl-count">
            {total === 0 ? 'No requests' : `${firstShown}–${lastShown} of ${total}`}
          </span>
          {/* Beside the count rather than down by the Previous/Next buttons: "how many am I
              looking at" and "how many should I see" are the same question, and the buttons are
              hidden entirely when everything fits on one page.

              THE OPTIONS ARE BARE NUMBERS AND THE UNIT IS SAID ONCE, in the label to the left
              (requester, 2026-09-10 — "the number only and only need to show page once"). It
              used to render `20 / page`, `50 / page`, `100 / page`, which repeated the unit
              three times in a list where the only thing that differs is the number, and made
              the closed trigger 110px wide to hold a word it already says beside itself.

              This is the SAME control the Devices list ships (`mdm-devices-page-size`): the
              `Rows per page` label, a 72px h-8 trigger, `cursor-pointer` on the trigger and
              every item, and an `aria-label` so the select is still named when the visible
              label is off to the side. Copied rather than re-decided — two paginated staff
              tables that pick their rows-per-page differently is the kind of small
              inconsistency that makes a console feel assembled from parts. */}
          <span className="text-xs text-muted-foreground">{'Rows per page'}</span>
          <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
            <SelectTrigger
              className="h-8 w-[72px] cursor-pointer"
              aria-label={'Rows per page'}
              data-testid="intl-page-size"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INTL_PAGE_SIZE_OPTIONS.map((n) => (
                <SelectItem key={n} value={String(n)} className="cursor-pointer">
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {sweep ? (
        <p className="text-sm text-muted-foreground" data-testid="intl-sweep-result">
          {sweep}
        </p>
      ) : null}

      {error ? (
        // The server's message verbatim — it names the missing DSN or the un-run migration
        // rather than saying "something went wrong", and which one it is decides who fixes it.
        <p className="text-sm text-destructive" data-testid="intl-list-error">
          {error.message}
        </p>
      ) : null}

      <div className="rounded-md border">
        {/* Read-only. The row's two buttons went with the action column — the poll re-reads
            invoices on its own, and Raise/Refresh live in the panel a row click opens. */}
        <IntlRequestTable
          requests={items}
          onSelect={(r) => setSelectedId(r.id)}
          selectedIds={selectedForExport}
          onToggleSelect={toggleForExport}
          onToggleSelectAll={(ids, select) =>
            setSelectedForExport((prev) => {
              const next = new Set(prev);
              for (const id of ids) {
                if (select) next.add(id);
                else next.delete(id);
              }
              return next;
            })
          }
        />
      </div>

      {/* The panel, not a route. See its own docblock — the queue is where the work is decided,
          and a full-page detour loses the filters that got you here. */}
      <IntlRequestDetailDrawer
        request={selected}
        onClose={() => setSelectedId(null)}
        onRaiseInvoice={setInvoicing}
      />

      <IntlExportDialog
        open={exporting}
        onOpenChange={setExporting}
        selectedIds={[...selectedForExport]}
        onExported={() => setSelectedForExport(new Set())}
      />

      {invoicing ? (
        <RaiseInvoiceDialog
          request={invoicing}
          open
          onOpenChange={(o) => {
            if (!o) setInvoicing(null);
          }}
        />
      ) : null}

      {total > pageSize ? (
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
            disabled={pageIndex === 0 || loading}
            data-testid="intl-prev"
          >
            {'Previous'}
          </Button>
          <Button
            variant="outline"
            onClick={() => setPageIndex((i) => i + 1)}
            disabled={lastShown >= total || loading}
            data-testid="intl-next"
          >
            {'Next'}
          </Button>
          {/* Which page of how many. Without it, Previous and Next are the only clue to where
              you are in a filtered set, and "did that do anything?" is the usual result. */}
          <span className="text-xs text-muted-foreground" data-testid="intl-page-of">
            {`Page ${pageIndex + 1} of ${Math.max(1, Math.ceil(total / pageSize))}`}
          </span>
        </div>
      ) : null}
    </div>
  );
}
